import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarX2 } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import ClassHeroCard from './ClassHeroCard';
import StepHeroCard, { AllDoneCard } from './StepHeroCard';
import ClassReminderStrip from './ClassReminderStrip';
import StepDots from './StepDots';
import { fill, stepVars } from '../../lib/nextSteps';

function WaitingCard({ data, vars, step }) {
  return (
    <section className="rounded-[28px] bg-white border border-amber-200 p-6 md:p-8 shadow-sm" data-testid="ns-hero" data-kind="waiting">
      <StepDots steps={data.steps} activeKey={step.key} />
      <div className="flex items-start gap-4 mt-4">
        <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0"><CalendarX2 size={22} /></div>
        <div>
          <h2 className="font-[Manrope] text-xl font-extrabold text-slate-900">{fill(step.title, vars)}</h2>
          <p className="text-sm text-slate-600 mt-1" data-testid="ns-class-note">{fill(data.texts.no_class_text, vars)}</p>
          <Link to="/group-classes" className="inline-block mt-3 text-xs font-semibold text-[#002FA7] hover:underline">See all classes →</Link>
        </div>
      </div>
    </section>
  );
}

/** Picks the hero (class card · step card · waiting · all done) and the slim class reminder underneath. */
export default function NextStepsHero({ data, onChanged }) {
  const vars = stepVars(data);
  const steps = data.steps || [];
  const hero = steps.find((s) => s.key === data.hero_key);
  const current = steps.find((s) => s.key === data.current_key);
  const cls = data.class;
  const allDone = steps.length > 0 && data.progress.done === data.progress.total;
  let card;
  if (allDone) card = <AllDoneCard texts={data.texts} vars={vars} steps={steps} />;
  else if (hero && hero.kind === 'class' && cls) card = <ClassHeroCard cls={cls} step={hero} steps={steps} texts={data.texts} vars={vars} onChanged={onChanged} />;
  else if (hero) card = <StepHeroCard step={hero} steps={steps} vars={vars} onChanged={onChanged} classNote={data.class_note_key ? fill(data.texts.no_class_text, vars) : null} />;
  else if (current) card = <WaitingCard data={data} vars={vars} step={current} />;
  const heroIsClass = !allDone && hero && hero.kind === 'class' && cls;
  const heroKey = allDone ? 'done' : `${hero?.key || current?.key || 'none'}-${heroIsClass ? (cls.seat_saved ? 'saved' : 'save') : 'step'}`;
  return (
    <div className="space-y-3" data-testid="ns-block" data-track={data.track}>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div key={heroKey} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}>
          {card}
        </motion.div>
      </AnimatePresence>
      {cls && !heroIsClass && <ClassReminderStrip cls={cls} texts={data.texts} vars={vars} onChanged={onChanged} />}
    </div>
  );
}
